import { useMemo, useState } from "react";
import {
  Button,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { parseFormSchema } from "@/lib/formSchema";
import { normalizeDealSubmissionFinancialSchema } from "@/lib/dealSubmissionFinancialLabels";
import type { FormField } from "@/lib/formSchema";
import { dealSubmissionApi } from "../../api/dealSubmission";
import { PortalApiError } from "../../api/client";
import type {
  DealSubmissionRichDto,
  DealSubmissionSectionDto,
} from "../../api/types";
import { sectionDisplayStatus } from "./ProgressRail";
import { outstandingEvaluationNames } from "./evaluationFreshness";

// Wizard Review & Submit. Lists every answered field per data section
// and every uploaded document, with "Edit" links back to the relevant
// section. Submit button is disabled when required sections, required
// documents, or current Evaluate & Feedback runs do not satisfy the same
// gate the server enforces.

interface ReviewAndSubmitProps {
  data: DealSubmissionRichDto;
  sections: DealSubmissionSectionDto[];
  onEditSection: (s: DealSubmissionSectionDto) => void;
  onSubmitted: () => void;
}

export function ReviewAndSubmit({ data, sections, onEditSection, onSubmitted }: ReviewAndSubmitProps) {
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const blockers = useMemo(() => buildBlockers(data, sections), [data, sections]);
  const editable =
    data.submission.status === "Draft" || data.submission.status === "ReturnedForChanges";
  const canSubmit = editable && blockers.length === 0;
  const submitLabel = data.submission.status === "ReturnedForChanges" ? "Resubmit" : "Submit deal";
  const busyLabel = data.submission.status === "ReturnedForChanges" ? "Resubmitting…" : "Submitting…";

  const submitDeal = useSubmittingMutation(
    async () => {
      await dealSubmissionApi.submit(data.submission.id);
      setSubmitted(true);
    },
    {
      onError: (err) => {
        if (err instanceof PortalApiError) {
          setSubmitErrors(err.errors.length > 0 ? err.errors : [err.message]);
        } else {
          setSubmitErrors([err instanceof Error ? err.message : "Couldn't submit your deal."]);
        }
      },
    },
  );

  function handleSubmit() {
    setSubmitErrors([]);
    void submitDeal.submit();
  }

  if (submitted) {
    return (
      <div className="flex flex-col gap-space-3" data-testid="wizard-submit-confirmation">
        <h2 className="text-h3 font-display text-onyx m-0">Submitted!</h2>
        <p className="text-small text-onyx m-0">
          Your deal submission is now with our liaison team. You'll see status
          updates on your dashboard as the review progresses.
        </p>
        <Button onClick={onSubmitted} data-testid="wizard-confirmation-back">
          Back to my deals
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-5">
      <header>
        <h2 className="text-h3 font-display text-onyx m-0">Review &amp; submit</h2>
        <p className="text-small text-gray-mid mt-space-2">
          Double-check your answers and documents before submitting. You can edit any
          section from here.
        </p>
      </header>

      {sections.map((section) => (
        <SectionSummary
          key={section.id}
          section={section}
          data={data}
          onEdit={() => onEditSection(section)}
        />
      ))}

      {blockers.length > 0 && (
        <div
          className="p-space-3 border border-status-warning rounded-md bg-status-warning/10"
          data-testid="wizard-blockers"
        >
          <p className="text-small font-medium text-onyx m-0">Before you can submit</p>
          <ul className="text-small text-onyx m-0 mt-space-2 pl-space-4 list-disc">
            {blockers.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        </div>
      )}

      {submitErrors.length > 0 && (
        <div className="text-small text-status-error m-0" role="alert" data-testid="submit-errors">
          {submitErrors.length === 1 ? (
            <p className="m-0">{submitErrors[0]}</p>
          ) : (
            <ul className="m-0 pl-space-4 list-disc">
              {submitErrors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          )}
        </div>
      )}

      {editable && (
        <div className="flex flex-col items-start gap-space-2">
          <SubmitButton
            onClick={handleSubmit}
            pending={submitDeal.pending}
            pendingLabel={busyLabel}
            disabled={!canSubmit}
            data-testid="wizard-final-submit"
          >
            {submitLabel}
          </SubmitButton>
          <SubmittingHint pending={submitDeal.pending} />
        </div>
      )}
    </div>
  );
}

function SectionSummary({
  section,
  data,
  onEdit,
}: {
  section: DealSubmissionSectionDto;
  data: DealSubmissionRichDto;
  onEdit: () => void;
}) {
  const status = sectionDisplayStatus(section, data);

  return (
    <section
      className="p-space-3 border border-border-light rounded-md bg-white-pure"
      data-testid={`review-section-${section.order}`}
    >
      <div className="flex items-baseline justify-between gap-space-3">
        <h3 className="text-small font-medium text-onyx m-0">
          {section.title}{" "}
          <span className="text-caption text-gray-mid">· {status}</span>
        </h3>
        <button
          type="button"
          onClick={onEdit}
          className="text-caption text-onyx underline"
          data-testid={`review-edit-${section.order}`}
        >
          Edit
        </button>
      </div>

      {section.kind === "DataEntry" ? (
        <DataAnswers section={section} data={data} />
      ) : (
        <DocumentSummary section={section} data={data} />
      )}
    </section>
  );
}

function DataAnswers({
  section,
  data,
}: {
  section: DealSubmissionSectionDto;
  data: DealSubmissionRichDto;
}) {
  const parsedSchema = parseFormSchema(section.formSchemaJson);
  const schema = parsedSchema ? normalizeDealSubmissionFinancialSchema(parsedSchema) : null;
  const state = data.sectionStates.find((s) => s.sectionId === section.id);
  const answers: Record<string, unknown> = state?.savedDataJson
    ? safeParse(state.savedDataJson)
    : {};

  if (!schema || schema.fields.length === 0) {
    return (
      <p className="text-caption text-gray-mid m-0 mt-space-2">
        No questions in this section.
      </p>
    );
  }

  const dataFields = schema.fields.filter(
    (f) => f.type !== "sectionheading" && f.type !== "instruction" && f.type !== "pagebreak",
  );

  return (
    <dl className="m-0 mt-space-2 grid grid-cols-1 md:grid-cols-2 gap-space-2">
      {dataFields.map((f) => (
        <div key={f.name} className="min-w-0">
          <dt className="text-caption text-gray-mid">{f.label}</dt>
          <dd className="text-small text-onyx m-0 truncate">{formatAnswer(answers[f.name], f)}</dd>
        </div>
      ))}
    </dl>
  );
}

function DocumentSummary({
  section,
  data,
}: {
  section: DealSubmissionSectionDto;
  data: DealSubmissionRichDto;
}) {
  const requests = data.documentRequests.filter((r) => r.sectionId === section.id);
  if (requests.length === 0) {
    return (
      <p className="text-caption text-gray-mid m-0 mt-space-2">
        No documents requested.
      </p>
    );
  }
  return (
    <ul className="list-none p-0 m-0 mt-space-2 flex flex-col gap-space-1">
      {requests.map((r) => {
        const doc = data.documents.find((d) => d.documentRequestId === r.id);
        return (
          <li
            key={r.id}
            className="text-caption text-onyx flex items-center justify-between gap-space-2"
          >
            <span className="truncate">
              {r.label}
              {r.required && <span className="text-status-error"> · required</span>}
            </span>
            <span className="text-caption text-gray-mid shrink-0">
              {doc ? `${doc.originalFileName} (${doc.scanStatus})` : "Not uploaded"}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function buildBlockers(
  data: DealSubmissionRichDto,
  sections: DealSubmissionSectionDto[],
): string[] {
  const out: string[] = [];
  const uploadedRequestIds = new Set(data.documents.map((d) => d.documentRequestId));

  for (const section of sections) {
    if (!section.required) continue;

    const sectionRequests = data.documentRequests.filter((r) => r.sectionId === section.id);
    const state = data.sectionStates.find((s) => s.sectionId === section.id) ?? null;
    const missingRequiredDocs = sectionRequests.filter(
      (r) => r.required && !uploadedRequestIds.has(r.id),
    );
    const outstandingEvaluations = outstandingEvaluationNames(
      section,
      state,
      sectionRequests,
      data.documents,
    );

    for (const r of missingRequiredDocs) {
      out.push(`"${r.label}" has not been uploaded. Return to "${section.title}" and upload it.`);
    }

    if (outstandingEvaluations.length > 0) {
      out.push(
        `Run Evaluate & Feedback on ${formatList(outstandingEvaluations)} in "${section.title}", then click Save & continue in that section.`,
      );
    }

    if (state?.status !== "Complete") {
      out.push(
        `"${section.title}" is not saved complete. Return to that section, resolve any uploads or Evaluate & Feedback prompts, then click Save & continue.`,
      );
    }
  }

  return out;
}

function formatList(items: string[]): string {
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function safeParse(blob: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(blob) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function formatAnswer(value: unknown, field: FormField): string {
  if (value === undefined || value === null || value === "") return "—";
  if (Array.isArray(value)) return value.length === 0 ? "—" : value.join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "—";
    }
  }
  if (field.type === "currency" && typeof value === "number") {
    return value.toLocaleString("en-US", { style: "currency", currency: "USD" });
  }
  return String(value);
}
